import { useEffect, useState, useCallback, useMemo } from 'react';
import { createRoute, redirect, useNavigate, useSearch, Link } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { usePersistedSearch } from '@/client/hooks/usePersistedSearch';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';
import { ClientSelector } from '@/client/components/client-selector';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import { ClientNameCell, EventIDCell, EventLinksCell } from '@/client/components/tables/cells';
import { API_BASE_URL } from '@/client/config';
import type { CaptionJobRow, CaptionJobsResponse, CaptionJobStatus, VttQualityScore, VttBatchAnalyzeResponse } from '@shared/types/captionJobs';
import { CAPTION_JOBS_CONFIG } from '@shared/types/captionJobs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Button } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from '@/client/components/ui/pagination';
import {
  RefreshCw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Circle,
} from 'lucide-react';

// Quick range presets
const QUICK_RANGES = [
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
  { label: '72h', hours: 72 },
  { label: '1 week', hours: 168 },
  { label: '2 weeks', hours: 336 },
  { label: '1 month', hours: 720 },
] as const;

type QuickRangeLabel = typeof QUICK_RANGES[number]['label'];

type CaptionJobsSearch = {
  clientID?: string;
  range?: QuickRangeLabel;
  status?: CaptionJobStatus;
  page?: number;
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'monitoring/caption-jobs',
  validateSearch: (search: Record<string, unknown>): CaptionJobsSearch => {
    return {
      clientID: typeof search.clientID === 'string' ? search.clientID : undefined,
      range: typeof search.range === 'string' ? search.range as QuickRangeLabel : undefined,
      status: typeof search.status === 'string' ? search.status as CaptionJobStatus : undefined,
      page: typeof search.page === 'number' ? search.page : undefined,
    };
  },
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: CaptionJobsComponent,
});

function getDateRange(rangeLabel: QuickRangeLabel | undefined) {
  const now = new Date();
  const range = QUICK_RANGES.find(r => r.label === rangeLabel) || QUICK_RANGES[0]; // Default 24hrs
  const start = new Date(now.getTime() - range.hours * 60 * 60 * 1000);
  return {
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(now, 'yyyy-MM-dd'),
  };
}

const STATUS_CONFIG: Record<CaptionJobStatus, { label: string; icon: typeof CheckCircle; className: string }> = {
  captioned: { label: 'Captioned', icon: CheckCircle, className: 'text-green-500' },
  missing: { label: 'Missing', icon: AlertTriangle, className: 'text-destructive' },
  pending: { label: 'Pending', icon: Circle, className: 'text-muted-foreground' },
};

function StatusIcon({ status }: { status: CaptionJobStatus }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <span title={config.label}>
      <Icon className={`h-4 w-4 ${config.className}`} />
    </span>
  );
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

const columnHelper = createColumnHelper<CaptionJobRow>();

/** Badge classes for quality score severity */
const SEVERITY_BADGE_CLASSES: Record<VttQualityScore['severity'], string> = {
  good: 'bg-green-500/15 text-green-700 border-green-500/25 hover:bg-green-500/25 dark:text-green-400',
  fair: 'bg-yellow-500/15 text-yellow-700 border-yellow-500/25 hover:bg-yellow-500/25 dark:text-yellow-400',
  poor: 'bg-red-500/15 text-red-700 border-red-500/25 hover:bg-red-500/25 dark:text-red-400',
};

function buildColumns(scores: Record<string, VttQualityScore>, scoresLoading: boolean) {
  return [
    columnHelper.accessor('status', {
      header: '',
      cell: (info) => <StatusIcon status={info.getValue()} />,
      size: 40,
      enableSorting: false,
    }),
    columnHelper.accessor('eventID', {
      header: 'Event ID',
      cell: (info) => (
        <EventIDCell eventID={info.getValue()} />
      ),
      size: 80,
    }),
    columnHelper.accessor('clientName', {
      header: 'Client',
      cell: (info) => (
        <ClientNameCell
          clientID={info.row.original.clientID}
          clientName={info.getValue()}
        />
      ),
      size: 180,
    }),
    columnHelper.accessor('sessionStart', {
      header: 'Session Start',
      cell: (info) => {
        const val = info.getValue();
        return val ? format(new Date(val), 'MMM d, yyyy HH:mm') : '-';
      },
      size: 160,
    }),
    columnHelper.accessor('captionDuration', {
      header: 'Duration',
      cell: (info) => formatDuration(info.getValue()),
      size: 100,
    }),
    columnHelper.accessor('publishedAt', {
      header: 'Published',
      cell: (info) => {
        const val = info.getValue();
        return val ? format(new Date(val), 'MMM d, HH:mm') : '-';
      },
      size: 130,
    }),
    columnHelper.accessor('timeToCaptionSeconds', {
      header: 'Time to Caption',
      cell: (info) => {
        const val = info.getValue();
        if (val === null) return '-';
        return formatDuration(val);
      },
      size: 130,
    }),
    columnHelper.accessor('captionFileSize', {
      header: 'VTT Size',
      cell: (info) => formatFileSize(info.getValue()),
      size: 100,
    }),
    columnHelper.display({
      id: 'qualityScore',
      header: 'Quality',
      size: 80,
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        if (row.status !== 'captioned' || !row.captionPath) {
          return <span className="text-muted-foreground">—</span>;
        }

        const score = scores[row.eventID];
        if (!score && scoresLoading) {
          return <Skeleton className="h-5 w-12" />;
        }
        if (!score) {
          return <span className="text-muted-foreground">—</span>;
        }

        return (
          <Link
            to="/utilities/vtt-analyzer"
            search={{ url: row.captionPath }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Badge
              variant="outline"
              className={`cursor-pointer ${SEVERITY_BADGE_CLASSES[score.severity]}`}
            >
              {score.score}
            </Badge>
          </Link>
        );
      },
    }),
    columnHelper.display({
      id: 'eventLinks',
      header: 'Links',
      cell: (info) => (
        <EventLinksCell
          eventID={info.row.original.eventID}
          clientID={info.row.original.clientID}
        />
      ),
      size: 70,
    }),
  ];
}

function CaptionJobsComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();
  const {
    clientID,
    range = '24h',
    status: statusFilter,
    page = 1,
  } = useSearch({ from: '/monitoring/caption-jobs' });

  usePersistedSearch('/monitoring/caption-jobs', {
    clientID: undefined,
    range: '24h',
    status: undefined,
    page: undefined,
  });

  const [data, setData] = useState<CaptionJobsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'sessionStart', desc: true }]);
  const [scores, setScores] = useState<Record<string, VttQualityScore>>({});
  const [scoresLoading, setScoresLoading] = useState(false);
  const [qualityFilter, setQualityFilter] = useState<VttQualityScore['severity'] | undefined>(undefined);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const { startDate, endDate } = getDateRange(range);

      const params = new URLSearchParams();
      if (clientID) params.set('clientID', clientID);
      if (statusFilter) params.set('status', statusFilter);
      params.set('startDate', startDate);
      params.set('endDate', endDate);
      params.set('page', String(page));
      params.set('limit', String(CAPTION_JOBS_CONFIG.DEFAULT_LIMIT));

      const response = await fetch(
        `${API_BASE_URL}/api/monitoring/caption-jobs?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 401) {
        logout({ logoutParams: { returnTo: window.location.origin } });
        return;
      }

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result: CaptionJobsResponse = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load caption jobs');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, logout, clientID, range, statusFilter, page]);

  const forceRefresh = useCallback(async () => {
    const token = await getAccessTokenSilently();
    await fetch(`${API_BASE_URL}/api/cache/invalidate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ pattern: 'caption-jobs:*' }),
    });
    loadData();
  }, [getAccessTokenSilently, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fetch quality scores for captioned rows on the current page
  useEffect(() => {
    if (!data?.jobs?.length) {
      setScores({});
      return;
    }

    const captionedJobs = data.jobs.filter(
      (j) => j.status === 'captioned' && j.captionPath
    );
    if (captionedJobs.length === 0) {
      setScores({});
      return;
    }

    let cancelled = false;
    const fetchScores = async () => {
      setScoresLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${API_BASE_URL}/api/vtt/analyze-batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            items: captionedJobs.map((j) => ({
              eventID: j.eventID,
              captionPath: j.captionPath,
              captionDuration: j.captionDuration,
            })),
          }),
        });
        if (res.ok && !cancelled) {
          const result: VttBatchAnalyzeResponse = await res.json();
          setScores(result.scores);
        }
      } catch (err) {
        console.error('Failed to fetch VTT quality scores:', err);
      } finally {
        if (!cancelled) setScoresLoading(false);
      }
    };

    fetchScores();
    return () => { cancelled = true; };
  }, [data?.jobs, getAccessTokenSilently]);

  const columns = useMemo(
    () => buildColumns(scores, scoresLoading),
    [scores, scoresLoading]
  );

  // Apply client-side quality filter
  const filteredJobs = useMemo(() => {
    const jobs = data?.jobs ?? [];
    if (!qualityFilter) return jobs;
    return jobs.filter((job) => {
      const score = scores[job.eventID];
      return score?.severity === qualityFilter;
    });
  }, [data?.jobs, qualityFilter, scores]);

  const table = useReactTable({
    data: filteredJobs,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const updateSearch = (updates: Partial<CaptionJobsSearch>) => {
    navigate({
      to: '/monitoring/caption-jobs',
      search: {
        clientID: 'clientID' in updates ? updates.clientID : clientID,
        range: 'range' in updates ? updates.range : range,
        status: 'status' in updates ? updates.status : statusFilter,
        // Reset page when filters change (unless page is being set explicitly)
        page: 'page' in updates ? updates.page : 1,
      },
    });
  };

  const hasFilters = clientID || statusFilter || range !== '24h' || qualityFilter;

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Live Caption Jobs</h1>
            <p className="text-sm text-muted-foreground">
              Monitor AI caption generation for published events
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CacheTimestamp cachedAt={data?.cacheInfo?.cachedAt ?? null} />
            <Button
              variant="ghost"
              size="icon"
              title="Force refresh"
              onClick={() => forceRefresh()}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        {data?.summary && (
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => updateSearch({ status: statusFilter === 'captioned' ? undefined : 'captioned' })}
              className={`rounded-lg border p-3 text-left transition-colors hover:bg-accent ${statusFilter === 'captioned' ? 'ring-2 ring-ring' : ''}`}
            >
              <div className="text-sm text-muted-foreground">Captioned</div>
              <div className="text-2xl font-bold">{data.summary.captioned}</div>
            </button>
            <button
              onClick={() => updateSearch({ status: statusFilter === 'missing' ? undefined : 'missing' })}
              className={`rounded-lg border p-3 text-left transition-colors hover:bg-accent ${statusFilter === 'missing' ? 'ring-2 ring-ring' : ''} ${data.summary.missing > 0 ? 'border-destructive' : ''}`}
            >
              <div className="text-sm text-muted-foreground">Missing</div>
              <div className={`text-2xl font-bold ${data.summary.missing > 0 ? 'text-destructive' : ''}`}>
                {data.summary.missing}
              </div>
            </button>
            <button
              onClick={() => updateSearch({ status: statusFilter === 'pending' ? undefined : 'pending' })}
              className={`rounded-lg border p-3 text-left transition-colors hover:bg-accent ${statusFilter === 'pending' ? 'ring-2 ring-ring' : ''}`}
            >
              <div className="text-sm text-muted-foreground">Pending</div>
              <div className="text-2xl font-bold">{data.summary.pending}</div>
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Range filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                {range}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {QUICK_RANGES.map(r => (
                <DropdownMenuItem
                  key={r.label}
                  onClick={() => updateSearch({ range: r.label })}
                >
                  {r.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Status filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                {statusFilter ? STATUS_CONFIG[statusFilter].label : 'All Statuses'}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => updateSearch({ status: undefined })}>
                All Statuses
              </DropdownMenuItem>
              {(Object.keys(STATUS_CONFIG) as CaptionJobStatus[]).map(s => (
                <DropdownMenuItem key={s} onClick={() => updateSearch({ status: s })}>
                  {STATUS_CONFIG[s].label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Quality score filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={scoresLoading && Object.keys(scores).length === 0}>
                {qualityFilter ? `Quality: ${qualityFilter.charAt(0).toUpperCase() + qualityFilter.slice(1)}` : 'All Qualities'}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setQualityFilter(undefined)}>
                All Qualities
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setQualityFilter('good')}>
                <span className="mr-2 h-2 w-2 rounded-full bg-green-500 inline-block" />
                Good (80-100)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setQualityFilter('fair')}>
                <span className="mr-2 h-2 w-2 rounded-full bg-yellow-500 inline-block" />
                Fair (50-79)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setQualityFilter('poor')}>
                <span className="mr-2 h-2 w-2 rounded-full bg-red-500 inline-block" />
                Poor (0-49)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Client selector, reset, refresh - right justified */}
          <div className="ml-auto flex items-center gap-2">
            <ClientSelector
              value={clientID ?? ''}
              onChange={(id, _client) => updateSearch({ clientID: id || undefined })}
              label=""
              className="min-w-[250px]"
            />
            {hasFilters && (
              <span onClick={() => setQualityFilter(undefined)}>
                <ResetFiltersButton
                  routePath="/monitoring/caption-jobs"
                  defaultValues={{ clientID: undefined, range: '24h', status: undefined }}
                />
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-lg border border-destructive p-4 text-destructive">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableHead
                      key={header.id}
                      className="cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{ width: header.getSize() }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() === 'asc' ? ' \u2191' :
                        header.column.getIsSorted() === 'desc' ? ' \u2193' : ''}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading && !data ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-8 text-muted-foreground">
                    No caption jobs found for the selected filters
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map(row => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted"
                    onClick={() => navigate({
                      to: '/events/$eventId',
                      params: { eventId: row.original.eventID },
                    })}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {data?.pagination && data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {data.pagination.page} of {data.pagination.totalPages} ({data.pagination.totalCount} total)
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => updateSearch({ page: page - 1 })}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </PaginationItem>
                <PaginationItem>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.pagination.totalPages}
                    onClick={() => updateSearch({ page: page + 1 })}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </Layout>
  );
}
